import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const initSession = async () => {
      // Parse tokens from URL hash (Supabase recovery links use hash fragments)
      const hash = window.location?.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params?.get('access_token');
        const refreshToken = params?.get('refresh_token');
        const type = params?.get('type');

        if (accessToken && refreshToken && type === 'recovery') {
          const { error: sessionErr } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionErr) {
            setSessionError('This password reset link is invalid or has expired. Please request a new one.');
            return;
          }
          setSessionReady(true);
          // Clean up the hash from the URL
          window.history?.replaceState(null, '', window.location?.pathname);
          return;
        }
      }

      // Fallback: listen for PASSWORD_RECOVERY event (some flows trigger this)
      const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setSessionReady(true);
        }
      });

      // Also check if there's already an active session
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session) {
        setSessionReady(true);
      } else if (!hash || !hash?.includes('access_token')) {
        setSessionError('This password reset link is invalid or has expired. Please request a new one.');
      }

      return () => subscription?.unsubscribe();
    };

    initSession();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password?.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase?.auth?.updateUser({ password });

      if (error) {
        throw error;
      }

      setSuccess(true);
      setTimeout(() => navigate('/signin'), 3000);
    } catch (err) {
      console.error('Password update error:', err);
      setError(err?.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set new password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded-lg text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-semibold mb-1">Password updated successfully!</p>
            <p className="text-sm">Redirecting you to sign in...</p>
          </div>
        ) : sessionError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-4 rounded-lg text-center">
            <p className="font-semibold mb-2">Link Invalid or Expired</p>
            <p className="text-sm mb-4">{sessionError}</p>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {!sessionReady && (
              <div className="text-center text-sm text-gray-500 py-2">
                Verifying reset link...
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="Enter new password (min. 8 characters)"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value)}
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !sessionReady || !password || !confirmPassword}
              className="w-full"
            >
              {loading ? 'Updating password...' : 'Update Password'}
            </Button>

            <div className="text-sm text-center">
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
